import React, { useEffect, useState, useRef, useCallback } from "react";
import { Container, Button, Typography, Grid, Skeleton } from "@mui/material";
import { trackPackage, downloadGuidePDF } from "../../api/guidesApi.js";
import { notification } from "../../utils/Notifications";
import { Search } from "@mui/icons-material";
import { useSearchParams } from "react-router-dom";
import styles from "./trackingStyles.js";
import Title from "../../components/SharedComponents/Title";
import SearchBar from "../../components/SharedComponents/SearchBar";
import useLogout from "../../hooks/auth/authHook.js";
import TrackingStatus from "../../components/Tracking/TrackingStatus/index.js";
import TrackingLastUpdate from "../../components/Tracking/TrackingLastUpdate/index.js";
import TrackingGuideDetails from "../../components/Tracking/TrackingGuideDetails/index.js";
import TrackingAddresses from "../../components/Tracking/TrackingAddresses/index.js";
import TrackingHistory from "../../components/Tracking/TrackingHistory/index.js";
import { I3DCubeScan } from "iconsax-react";
import TrackingResponsible from "../../components/Tracking/TrackingResponsible/index.js";
import TrackingPDFButtons from "../../components/Tracking/TrackingPDFButtons/index.js";

const Tracking = () => {
  const [logout] = useLogout();
  const logoutRef = useRef(logout);
  const [searchParams, setSearchParams] = useSearchParams();
  const codeFromUrl = searchParams.get("code");
  const lastSearchedCode = useRef(null);

  const [guide, setGuide] = useState(null);
  const [search, setSearch] = useState(codeFromUrl || "");
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingPDFLinks, setIsLoadingPDFLinks] = useState(false);
  const [pdfLink, setPdfLink] = useState(null);
  const [termicaLink, setTermicaLink] = useState(null);

  // Actualizar la referencia cuando logout cambie
  useEffect(() => {
    logoutRef.current = logout;
  }, [logout]);

  // Función para obtener los links de PDF
  const fetchPDFLinks = useCallback((trackingCode) => {
    if (!trackingCode) {
      setPdfLink(null);
      setTermicaLink(null);
      return;
    }

    setIsLoadingPDFLinks(true);
    downloadGuidePDF(trackingCode)
      .then((response) => {
        if (response.data.status_Code === 200) {
          const pdfUrls = response.data.pdf_urls || {};
          
          const letterUrl = pdfUrls.letter && typeof pdfUrls.letter === 'string' && pdfUrls.letter.trim() !== '' 
            ? pdfUrls.letter.trim() 
            : null;
          const laserUrl = pdfUrls.laser && typeof pdfUrls.laser === 'string' && pdfUrls.laser.trim() !== '' 
            ? pdfUrls.laser.trim() 
            : null;

          setPdfLink(letterUrl);
          setTermicaLink(laserUrl);
        } else if (response.data.status_Code === 401) {
          notification({
            text: "Sesión expirada. Por favor, inicia sesión nuevamente.",
            type: "error",
          });
          logoutRef.current();
          setPdfLink(null);
          setTermicaLink(null);
        } else {
          setPdfLink(null);
          setTermicaLink(null);
        }
      })
      .catch((error) => {
        setPdfLink(null);
        setTermicaLink(null);
      })
      .finally(() => {
        setIsLoadingPDFLinks(false);
      });
  }, []);

  const trackGuide = useCallback((trackingCode) => {
    setIsLoading(true);
    trackPackage(trackingCode)
      .then((response) => {
        if (response.data.status_Code === 200) {
          const guideWithEvidence = normalizeGuideData(response.data);
          setGuide(guideWithEvidence);
          // Obtener los links de PDF después de obtener la guía
          fetchPDFLinks(trackingCode);
          setIsLoading(false);
        } else if (response.data.status_Code === 401) {
          notification({
            text: "Sesión expirada. Por favor, inicia sesión nuevamente.",
            type: "error",
          });
          setGuide(null);
          setIsLoading(false);
          logout();
        } else {
          notification({
            text: "Error al obtener los detalles de la guía",
            type: "error",
          });
          setIsLoading(false);
          setGuide(null);
        }
      })
      .catch(() => {
        notification({
          text: "Error de conexión, revisa tu conexión a internet",
          type: "error",
        });
        setIsLoading(false);
        setGuide(null);
      });
  }, [logout, fetchPDFLinks]);

  // Efecto para buscar automáticamente cuando hay un código en la URL
  useEffect(() => {
    if (codeFromUrl && codeFromUrl.trim() !== "" && lastSearchedCode.current !== codeFromUrl) {
      lastSearchedCode.current = codeFromUrl;
      setSearch(codeFromUrl);
      trackGuide(codeFromUrl);
    } else if (!codeFromUrl) {
      // Resetear cuando no hay código en la URL
      lastSearchedCode.current = null;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [codeFromUrl]);

  const handleSearchClick = () => {
    trackGuide(search);
    // Actualizar la URL con el código de búsqueda
    if (search && search.trim() !== "") {
      setSearchParams({ code: search });
      lastSearchedCode.current = search;
    }
  };

  useEffect(() => {
    // Solo limpiar si el search cambia manualmente y no es por el código de la URL
    if (search && search !== codeFromUrl && lastSearchedCode.current !== search) {
      setGuide(null);
      setPdfLink(null);
      setTermicaLink(null);
    }
  }, [search, codeFromUrl]);

  // Función para normalizar datos de la guía usando los datos reales del API
  const normalizeGuideData = (guideData) => {
    if (!guideData) return guideData;

    const trackingCode = guideData.guide_info?.tracking_code || "";

    // Normalizar guides_history usando los datos reales del API
    const enrichedHistory = guideData.guides_history?.map((entry) => {
      const historyEntry = { ...entry };
      
      // Normalizar el status: si es un objeto, extraer el keyname
      if (typeof entry.status === 'object' && entry.status !== null) {
        historyEntry.status = entry.status.keyname || entry.status.type || 'Desconocido';
      } else {
        historyEntry.status = entry.status || 'Desconocido';
      }
      
      // Mapear user.full_name a delivery_man
      if (entry.user && entry.user.full_name) {
        historyEntry.delivery_man = entry.user.full_name;
      }
      
      // Mapear photos array a evidence_images
      if (entry.photos && Array.isArray(entry.photos) && entry.photos.length > 0) {
        historyEntry.evidence_images = entry.photos.map(photo => 
          typeof photo === 'string' ? photo : photo.url || photo
        );
      }
      
      // Mapear latitude y longitude a location object
      if (entry.latitude !== null && entry.longitude !== null) {
        historyEntry.location = {
          latitude: entry.latitude.toString(),
          longitude: entry.longitude.toString(),
        };
      }
      
      // Agregar fecha de evidencia si no existe (usar date del entry)
      if (!historyEntry.evidence_date && entry.date) {
        historyEntry.evidence_date = entry.date;
      }

      return historyEntry;
    }) || guideData.guides_history;

    // Datos de PDFs (dummy fallback si no vienen del API)
    const pdfUrls = {
      carta: guideData.guide_info?.pdf_carta_url 
        || guideData.pdf_info?.carta_url 
        || guideData.pdf_urls?.carta 
        || `https://picsum.photos/seed/${trackingCode}-carta/800/600`,
      termica: guideData.guide_info?.pdf_termica_url 
        || guideData.pdf_info?.termica_url 
        || guideData.pdf_urls?.termica 
        || `https://picsum.photos/seed/${trackingCode}-termica/400/600`,
    };

    return {
      ...guideData,
      guides_history: enrichedHistory,
      // Mantener evidence_data vacío o con estructura básica si es necesario
      evidence_data: guideData.evidence_data || {
        delivery_attempts: [],
        delivery: null,
        price_adjustments: [],
        process_evidence: [],
      },
      pdf_urls: {
        carta: guideData.guide_info?.pdf_carta_url || pdfUrls.carta,
        termica: guideData.guide_info?.pdf_termica_url || pdfUrls.termica,
      },
    };
  };



  return (
    <Container
      style={styles.principalContainer}
    >
      <Title title="Rastreo" />
      <SearchBar setSearch={setSearch} value={search}>
        <Button
          variant="contained"
          startIcon={<Search />}
          onClick={handleSearchClick}
          style={styles.addUserButton}
        >
          Buscar
        </Button>
      </SearchBar>

      {isLoading ? (
        <Container sx={styles.nonPadding}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={12} lg={6}>
              <Container sx={styles.mainContainer}>
                <Skeleton variant="rounded" width={'60%'} height={30} />
                <Skeleton variant="rounded" width={'100%'} height={54} />
              </Container>
            </Grid>
            <Grid item xs={12} md={12} lg={6}>
              <Container sx={styles.mainContainer}>
                <Skeleton variant="rounded" width={'60%'} height={30} />
                <Skeleton variant="rounded" width={'100%'} height={54} />
              </Container>
            </Grid>
            <Grid item xs={12} md={12} lg={6}>
              <Container sx={styles.mainContainer}>
                <Skeleton variant="rounded" width={'60%'} height={30} />
                <Skeleton variant="rounded" width={'100%'} height={54} />
                <Container sx={styles.skeletonContainer}>
                  <Skeleton variant="rounded" width={'33%'} height={54} />
                  <Skeleton variant="rounded" width={'33%'} height={54} />
                  <Skeleton variant="rounded" width={'33%'} height={54} />
                </Container>
                <Container sx={styles.skeletonContainer}>
                  <Skeleton variant="rounded" width={'50%'} height={54} />
                  <Skeleton variant="rounded" width={'50%'} height={54} />
                </Container>
              </Container>
            </Grid>
            <Grid item xs={12} md={12} lg={6}>
              <Container sx={styles.mainContainer}>
                <Skeleton variant="rounded" width={'60%'} height={30} />
                <Container sx={styles.skeletonContainer}>
                  <Skeleton variant="rounded" width={'50%'} height={180} />
                  <Skeleton variant="rounded" width={'50%'} height={180} />
                </Container>
              </Container>
            </Grid>
            <Grid item xs={12}>
              <Container sx={styles.mainContainer}>
                <Skeleton variant="rounded" width={'30%'} height={30} />
                <Container sx={styles.skeletonHistoryContainer}>
                  <Skeleton variant="rounded" width={'30%'} height={40} />
                  <Skeleton variant="circular" width={40} height={40} />
                  <Skeleton variant="rounded" width={'30%'} height={40} />
                </Container>
                <Container sx={styles.skeletonHistoryContainer}>
                  <Skeleton variant="rounded" width={'30%'} height={40} />
                  <Skeleton variant="circular" width={40} height={40} />
                  <Skeleton variant="rounded" width={'30%'} height={40} />
                </Container>
                <Container sx={styles.skeletonHistoryContainer}>
                  <Skeleton variant="rounded" width={'30%'} height={40} />
                  <Skeleton variant="circular" width={40} height={40} />
                  <Skeleton variant="rounded" width={'30%'} height={40} />
                </Container>
                <Container sx={styles.skeletonHistoryContainer}>
                  <Skeleton variant="rounded" width={'30%'} height={40} />
                  <Skeleton variant="circular" width={40} height={40} />
                  <Skeleton variant="rounded" width={'30%'} height={40} />
                </Container>
              </Container>
            </Grid>
          </Grid>
        </Container>
      ) : search !== "" && guide ? (
        <Container sx={{ padding: "0px !important" }}>
          <Grid container spacing={2}>
            { guide?.guide_info?.last_owner && (
              <Grid item xs={12} md={12} lg={8}>
                <TrackingResponsible guide={guide} />
              </Grid>
            )}
            {(pdfLink || termicaLink || isLoadingPDFLinks) && (
              <Grid item xs={12} md={12} lg={guide?.guide_info?.last_owner ? 4 : 12}>
                <TrackingPDFButtons 
                  pdfLink={pdfLink}
                  termicaLink={termicaLink}
                  isLoadingLinks={isLoadingPDFLinks}
                  trackingCode={search}
                />
              </Grid>
            )}
            <Grid item xs={12} md={12} lg={6}>
              <TrackingStatus guide={guide} onGuideUpdated={() => trackGuide(search)} trackingCode={search} />
            </Grid>
            <Grid item xs={12} md={12} lg={6}>
              <TrackingLastUpdate guide={guide} />
            </Grid>
            <Grid item xs={12} md={12} lg={6}>
              <TrackingGuideDetails guide={guide} />
            </Grid>
            <Grid item xs={12} md={12} lg={6}>
              <TrackingAddresses guide={guide} />
            </Grid>
            <Grid item xs={12}>
              <TrackingHistory guide={guide} />
            </Grid>
          </Grid>
        </Container>
      ) : (
        <Container sx={{...styles.mainContainer, textAlign: 'center'}}>
          <Container sx={{padding: '0px !important'}}>
            <I3DCubeScan size={48} />
            <Typography variant="h6" sx={{fontWeight: 'bold', marginTop: '12px'}}>Sin código de guía</Typography>
            <Typography>Ingresa el código de una guía para continuar</Typography>
          </Container>
        </Container>
      )}
    </Container>
  );
};

export default Tracking;
